import { AppBar, Button, Dialog, IconButton, Slide, Toolbar, Typography, Box, Slider, Stack } from '@mui/material'
import React, { forwardRef, useEffect, useState } from 'react'
import Cropper, { Area } from 'react-easy-crop'
import CloseIcon from '@mui/icons-material/Close'
import { TransitionProps } from '@mui/material/transitions'
import { ZoomIn, ZoomOut } from '@mui/icons-material'
import { useTranslation } from 'react-i18next'
import { useLayoutSideSize } from './GlobalLayout/GlobalLayout'

const ICON_SIZE = 320
const MAX_ZOOM = 6

const transition = forwardRef(function Transition (
  props: TransitionProps & {children: React.ReactElement<unknown>},
  ref: React.Ref<unknown>,
) {
  return <Slide direction='up' ref={ref} {...props}/>
})

const getCroppedImage = async (image_url: string, area: Area) => {
  const image = await new Promise<HTMLImageElement>((resolve, reject)=> {
    Object.assign(new Image(), {
      crossOrigin: 'anonymous',
      src: image_url,
      onload (this: HTMLImageElement) { resolve(this) },
      onerror (err: Error) { reject(err) },
    })
  })
  const canvas = document.createElement('canvas')
  canvas.width = ICON_SIZE
  canvas.height = ICON_SIZE
  const ctx = canvas.getContext('2d', {
    colorSpace: 'srgb' // HDR情報を捨てる
  })
  if (!ctx) throw new Error('Failed to get canvas context')
  ctx.drawImage(
    image,
    area.x * 0.01 * image.naturalWidth,
    area.y * 0.01 * image.naturalHeight,
    area.width * 0.01 * image.naturalWidth,
    area.height * 0.01 * image.naturalHeight,
    0,
    0,
    ICON_SIZE,
    ICON_SIZE,
  )
  return canvas.toDataURL('image/jpeg', 0.93)
}

type Props = {
  edit_image: string
  close: () => void
  setCroppedImage: (data_url: string)=> void
}
const IconMaker: React.FC<Props> = ({edit_image, close, setCroppedImage}) => {
  const {t} = useTranslation()
  const side_size = useLayoutSideSize()
  const is_layout_slim = side_size === 0
  const [zoom, setZoom] = useState(1)
  const [crop, setCrop] = useState({ x: 0, y: 0 })
  const [cropped_area, setCroppedArea] = useState({ x: 0, y: 0, width: 0, height: 0 })
  useEffect(()=> {
    setZoom(1)
    setCrop({ x: 0, y: 0 })
    setCroppedArea({ x: 0, y: 0, width: 0, height: 0 })
  }, [edit_image])

  return <Dialog
    open={Boolean(edit_image)}
    onClose={close}
    fullScreen
    slots={{ transition }}
  >
    <AppBar color='header' enableColorOnDark elevation={0} position='sticky'>
      <Toolbar>
        <IconButton
          edge='start'
          color='inherit'
          onClick={close}
          aria-label='close'
          sx={{mr: 1}}
        >
          <CloseIcon/>
        </IconButton>
        <Typography
          variant='h3' component='div' fontSize='medium'
          sx={{
            flexGrow: 1,
            mr: 1,
            fontWeight: 'bold',
            whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
            textAlign: is_layout_slim ? 'center' : 'left'
          }}
        >
          {t('upload.edit_icon')}
        </Typography>
        <Button
          variant='contained'
          color='primary'
          size='small'
          sx={{ ml: 2 }}
          startIcon={<span className='material-icons-outlined'>check_circle</span>}
          onClick={()=> {
            getCroppedImage(edit_image, cropped_area)
              .then(setCroppedImage)
          }}
        >
          {t('general.complate')}
        </Button>
      </Toolbar>
    </AppBar>
    <Cropper
      image={edit_image}
      crop={crop}
      zoom={zoom}
      aspect={1}
      cropShape='round'
      showGrid={false}
      maxZoom={MAX_ZOOM}
      onCropChange={setCrop}
      onZoomChange={setZoom}
      onCropAreaChange={setCroppedArea}
      style={{
        containerStyle: {
          width: '100%',
          maxHeight: 'calc(100vh - 124px)',
          top: '62px',
        },
        cropAreaStyle: {
          border: 'none'
        }
      }}
    />
    <Box
      width='100%' height='62px'
      sx={{
        bgcolor: 'background.paper',
        position: 'absolute',
        bottom: 0,
        alignContent: 'center',
      }}
    >
      <Box width='320px' margin='auto'>
        <Stack spacing={2} direction='row' alignItems='center'>
          <ZoomOut/>
          <Slider
            value={zoom}
            min={1}
            max={MAX_ZOOM}
            step={0.1}
            onChange={(e, v)=> { setZoom(v) }}
          />
          <ZoomIn/>
        </Stack>
      </Box>
    </Box>
  </Dialog>
}
export default IconMaker